import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Profile.module.css';
import LoginHistory from '../../components/LoginHistory/LoginHistory';
import { userService } from '../../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [userStats, setUserStats] = useState({
    proposalComparisons: 0,
    contractorsEvaluated: 0,
    potentialSavings: 0,
    aiInsights: 0,
    riskFlagsDetected: 0,
    timeSavedHours: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    jobTitle: '',
    phone: '',
    profileImage: null
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (!userData) {
      navigate('/login');
      return;
    }
    setUser(userData);
    setFormData({
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      companyName: userData.companyName || '',
      jobTitle: userData.jobTitle || '',
      phone: userData.phone || '',
      profileImage: userData.profileImage || null
    });
    
    // Fetch user statistics
    fetchUserStats();
  }, [navigate]);

  const fetchUserStats = async () => {
    try {
      setIsLoadingStats(true);
      const response = await userService.getUserStats();
      if (response.data && response.data.status === 'success') {
        setUserStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Keep default values if error occurs
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          profileImage: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // TODO: Implement API call to update profile
    setIsEditing(false);
    // For now, just update local storage
    localStorage.setItem('user', JSON.stringify({ ...user, ...formData }));
    setUser(prev => ({ ...prev, ...formData }));
  };

  const handleLoginHistoryClick = () => {
    setShowLoginHistory(true);
  };

  if (!user) return null;

  return (
    <div className={styles.profileWrapper}>
      <div className={styles.profileHeader}>
        <div className={styles.headerContent}>
          <h1>My Profile</h1>
          <p>Manage your account settings and preferences</p>
        </div>
      </div>

      <div className={styles.profileContainer}>
        <div className={styles.mainContent}>
          <div className={styles.profileSection}>
          <div className={styles.profileImage}>
            {formData.profileImage ? (
              <img src={formData.profileImage} alt="Profile" />
            ) : (
              <div className={styles.initials}>
                {formData.firstName?.[0]}{formData.lastName?.[0]}
              </div>
            )}
            {isEditing && (
              <label className={styles.uploadButton}>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  hidden 
                />
                <i className="fas fa-camera"></i>
              </label>
            )}
        </div>

            <div className={styles.personalInfoSection}>
          <div className={styles.actionHeader}>
            <h2>Personal Information</h2>
            <button 
              className={styles.editButton}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? (
                <>
                  <i className="fas fa-times"></i>
                  Cancel
                </>
              ) : (
                <>
                  <i className="fas fa-edit"></i>
                  Edit Profile
                </>
              )}
            </button>
          </div>

          <form onSubmit={handleSubmit} className={styles.profileForm}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </div>
            </div>

            {isEditing && (
              <div className={styles.formActions}>
                <button type="submit" className={styles.saveButton}>
                  <i className="fas fa-save"></i>
                  Save Changes
                </button>
              </div>
            )}
          </form>
            </div>
          </div>

          <div className={styles.profileStats}>
            <div className={styles.statItem}>
              <i className="fas fa-file-contract"></i>
              <div className={styles.statInfo}>
                <h3>Proposal Comparisons</h3>
                <span>
                  {isLoadingStats ? '...' : `${userStats.proposalComparisons} Analyzed`}
                </span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-users"></i>
              <div className={styles.statInfo}>
                <h3>Contractors Evaluated</h3>
                <span>
                  {isLoadingStats ? '...' : `${userStats.contractorsEvaluated} Total`}
                </span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-dollar-sign"></i>
              <div className={styles.statInfo}>
                <h3>Potential Savings</h3>
                <span>
                  {isLoadingStats ? '...' : `$${userStats.potentialSavings.toLocaleString()} Identified`}
                </span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-brain"></i>
              <div className={styles.statInfo}>
                <h3>AI Insights</h3>
                <span>
                  {isLoadingStats ? '...' : `${userStats.aiInsights} Generated`}
                </span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-exclamation-triangle"></i>
              <div className={styles.statInfo}>
                <h3>Risk Flags Detected</h3>
                <span>
                  {isLoadingStats ? '...' : `${userStats.riskFlagsDetected} Found`}
                </span>
              </div>
            </div>
            <div className={styles.statItem}>
              <i className="fas fa-clock"></i>
              <div className={styles.statInfo}>
                <h3>Time Saved</h3>
                <span>
                  {isLoadingStats ? '...' : `${userStats.timeSavedHours} Hours`}
                </span>
              </div>
            </div>
          </div>

          <LoginHistory 
            isOpen={showLoginHistory} 
            onClose={() => setShowLoginHistory(false)} 
          />
        </div>
      </div>
    </div>
  );
};

export default Profile; 